<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { api } from '../lib/api.ts';
  import { storageEstimate } from '../lib/db.ts';
  import { nativeAvailable } from '../lib/scan/engine.ts';
  import { HOLD_PRESETS, audioState, chime, haptic, primeAudio } from '../lib/feedback.ts';
  import type { Tone } from '../lib/scan/outcome.ts';

  let authMode = $state<'dev' | 'oidc' | null>(null);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let storage = $state<{ usage: number; quota: number } | null>(null);
  let native = $state<boolean | null>(null);

  $effect(() => {
    void (async () => {
      try {
        const res = await fetch('/auth/mode', { credentials: 'include' });
        if (res.ok) authMode = (await res.json()).mode;
      } catch {
        /* offline — the sign-in section simply stays unavailable */
      }
      storage = await storageEstimate();
      native = await nativeAvailable();
      audio = audioState();
    })();
  });


  async function logout() {
    await api.logout().catch(() => undefined);
    await store.setIdentity(null);
  }

  const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} Mo`;

  const TONE_TESTS: { tone: Tone; label: string }[] = [
    { tone: 'ok', label: 'Accepté' },
    { tone: 'warn', label: 'Averti' },
    { tone: 'danger', label: 'Refusé' },
  ];

  /** Playing the cue is itself the gesture that lets the audio context start. */
  let audio = $state<'running' | 'suspended' | 'unsupported'>('suspended');
  function tryTone(tone: Tone) {
    primeAudio();
    haptic(tone);
    if (store.scanSound) chime(tone);
    // Reading it straight away would catch the context mid-resume.
    setTimeout(() => (audio = audioState()), 300);
  }
</script>

<h1>Réglages</h1>

<div class="card">
  <h2>Compte</h2>
  {#if store.identity}
    <p class="small">
      Connecté comme <strong>{store.identity.display_name}</strong>
      <span class="muted mono tiny">({store.identity.user_id})</span>
    </p>
    <div class="row wrap">
      {#each store.identity.roles as r (r)}<span class="pill accent">{r}</span>{/each}
    </div>
    <button class="slim ghost" style="margin-top:.6rem" onclick={logout}>Se déconnecter</button>
    {#if authMode === 'dev'}
      <div class="banner warn" style="margin-top:.6rem">
        Mode développement : identité locale, sans SSO. À remplacer par Zitadel
        (<code>AUTH_MODE=oidc</code>) avant tout usage réel.
      </div>
    {/if}
  {/if}
  {#if error}<div class="banner danger" style="margin-top:.6rem">{error}</div>{/if}
</div>

<div class="card">
  <h2>Synchronisation</h2>
  <ul class="list">
    <li><span class="grow small">État</span><span class="pill {store.online ? 'ok' : 'warn'}">{store.online ? 'en ligne' : 'hors connexion'}</span></li>
    <li><span class="grow small">En attente d'envoi</span><span class="pill {store.pending ? 'accent' : 'ok'}">{store.pending}</span></li>
    <li><span class="grow small">Dernière synchro</span><span class="tiny muted">{store.lastSync ? new Date(store.lastSync).toLocaleString('fr-FR') : 'jamais'}</span></li>
    <li><span class="grow small">Événements locaux</span><span class="tiny muted">{store.events.length}</span></li>
  </ul>
  <button class="primary block" onclick={() => store.sync()} disabled={store.syncing}>
    {#if store.syncing}<span class="spinner"></span>{/if} Synchroniser maintenant
  </button>

  {#if store.rejections.length > 0}
    <h3 style="margin-top:.8rem">Refusés par le serveur ({store.rejections.length})</h3>
    <p class="tiny muted">
      Définitivement inacceptables, donc retirés de la file plutôt que réessayés sans fin.
      Conservés ici parce qu'un scan refusé reste un fait à expliquer.
    </p>
    <ul class="list">
      {#each store.rejections.slice(0, 20) as r (r.id)}
        <li>
          <span class="grow tiny mono truncate">{r.detail ?? r.id}</span>
          <span class="pill danger">{r.reason}</span>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<div class="card">
  <h2>Appareil</h2>
  <ul class="list">
    <li><span class="grow small">Identifiant</span><span class="tiny mono muted truncate">{store.device}</span></li>
    <li>
      <span class="grow small">Stockage persistant</span>
      <span class="pill {store.persisted ? 'ok' : 'warn'}">{store.persisted ? 'accordé' : 'non accordé'}</span>
    </li>
    {#if storage}
      <li><span class="grow small">Espace utilisé</span><span class="tiny muted">{mb(storage.usage)} / {mb(storage.quota)}</span></li>
    {/if}
    <li>
      <span class="grow small">Lecteur QR natif</span>
      <span class="pill {native ? 'ok' : 'warn'}">{native === null ? '…' : native ? 'disponible' : 'absent — WASM'}</span>
    </li>
  </ul>
  {#if !store.persisted}
    <p class="tiny muted">
      Le navigateur n'a pas garanti la conservation des données locales : sous forte pression
      de stockage, il pourrait les effacer. Gardez un œil sur le nombre d'éléments en attente,
      et synchronisez dès que possible.
    </p>
  {/if}
</div>


<div class="card">
  <h2>Retour après un scan</h2>
  <p class="tiny muted">
    Un code est décodé plus vite qu'il ne se lit. Après chaque chariot, le scanner marque
    donc une pause sur le résultat avant de prendre le suivant.
  </p>
  <div class="segmented" role="group" aria-label="Durée de la pause">
    {#each HOLD_PRESETS as preset (preset.ms)}
      <button
        aria-pressed={store.scanHoldMs === preset.ms}
        onclick={() => store.setScanHold(preset.ms)}
      >
        {preset.label}
      </button>
    {/each}
  </div>
  <label class="row" style="gap:.6rem;cursor:pointer">
    <input
      type="checkbox"
      style="width:auto;min-height:auto"
      checked={store.scanSound}
      onchange={(e) => store.setScanSound(e.currentTarget.checked)}
    />
    <span class="small grow">Bip à chaque scan</span>
  </label>
  <p class="tiny muted">
    Le son et la vibration diffèrent selon le résultat — deux notes montantes pour un scan
    accepté : on peut continuer à marcher et savoir qu'un scan a été refusé sans regarder
    l'écran.
  </p>
  <ul class="list">
    <li>
      <span class="grow small">Sortie audio</span>
      <span class="pill {audio === 'running' ? 'ok' : audio === 'unsupported' ? 'danger' : 'warn'}">
        {audio === 'running' ? 'active' : audio === 'unsupported' ? 'indisponible' : "en attente d'un appui"}
      </span>
    </li>
  </ul>
  <p class="tiny muted">
    Un navigateur n'autorise le son qu'après un premier appui sur l'écran ; le bouton
    ci-dessous suffit à le vérifier. Sur iPhone, le petit interrupteur latéral coupe aussi ce
    son : s'il est sur silencieux, la vibration reste, pas le bip.
  </p>
  <div class="row wrap" style="margin-top:.4rem">
    {#each TONE_TESTS as t (t.tone)}
      <button class="slim" onclick={() => tryTone(t.tone)}>Tester : {t.label}</button>
    {/each}
  </div>
</div>

<div class="card">
  <h2>Diagnostic</h2>
  <label class="row" style="gap:.6rem;cursor:pointer">
    <input
      type="checkbox"
      style="width:auto;min-height:auto"
      checked={store.forceFallbackScanner}
      onchange={(e) => (store.forceFallbackScanner = e.currentTarget.checked)}
    />
    <span class="small grow">Forcer le lecteur WASM</span>
  </label>
  <p class="tiny muted">
    Le lecteur de secours ne sert qu'aux navigateurs sans <code>BarcodeDetector</code>. Sans
    cette option, il ne serait jamais testé pendant le développement, puisqu'on travaille sous
    Chrome.
  </p>
</div>
